/**
 * sl-oll9.16: DRY helpers for common block operations.
 */

/**
 * Creates a findDefaultBlockId function that searches for a block
 * with the given flavour among a doc's root children.
 *
 * Eliminates duplicated lambda in fullscreen view registrations.
 *
 * @example
 * fullscreenRegistry.register({
 *   findDefaultBlockId: findBlockByFlavour('affine:database'),
 * });
 */
export function findBlockByFlavour(flavour: string) {
  return (doc: {
    root?: { children?: { id: string; flavour: string }[] } | null;
  }): string | null => {
    const children = doc.root?.children ?? [];
    return children.find(c => c.flavour === flavour)?.id ?? null;
  };
}

/**
 * Creates a BlockViewExtension that renders different Lit tags
 * depending on whether the block is inside a note (page mode)
 * or on a surface (canvas/edgeless mode).
 *
 * Forward-looking utility for sl-9qm ViewComponent blocks.
 *
 * @example
 * import { BlockViewExtension } from '@blocksuite/affine/std';
 * import { literal } from 'lit/static-html.js';
 *
 * // Instead of:
 * // BlockViewExtension('my-flavour', model => {
 * //   return model.parent?.flavour === 'affine:note' ? docTag : canvasTag;
 * // });
 *
 * // Use:
 * createDocCanvasViewSelector('affine:note', literal`my-doc-view`, literal`my-canvas-view`);
 */
export function createDocCanvasViewSelector(
  noteParentFlavour: string,
  docTag: unknown,
  canvasTag: unknown
) {
  return (model: { parent?: { flavour: string } | null }): unknown => {
    return model.parent?.flavour === noteParentFlavour ? docTag : canvasTag;
  };
}
